import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const RATING_OPTIONS = ['Excellent', 'Good', 'Fair', 'Poor'];
const RECOMMEND_OPTIONS = ['Yes', 'No', 'Maybe'];

const EVALUATION_ITEMS = [
  { key: 'pre_rotation_communication', label: 'Pre-rotation communication was clear' },
  { key: 'orientation_organized', label: 'Orientation on arrival was well organized' },
  { key: 'learning_opportunities', label: 'Learning opportunities were valuable' },
  { key: 'supervisor_support', label: 'Supervisor support and teaching were effective' },
  { key: 'staff_professional', label: 'Department staff were professional and supportive' },
  { key: 'department_organized', label: 'Department processes/workflow were well organized' },
  { key: 'ata_coordination', label: 'ATA coordination and support were satisfactory' },
  { key: 'overall_experience', label: 'Overall rotation experience met expectations' },
];

export default function TraineeEvaluation() {
  const { traineeId } = useParams();
  const [trainee, setTrainee] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    pre_rotation_communication: '',
    orientation_organized: '',
    learning_opportunities: '',
    supervisor_support: '',
    staff_professional: '',
    department_organized: '',
    ata_coordination: '',
    overall_experience: '',
    best_part: '',
    suggestions: '',
    recommend: '',
    overall_rating: '',
  });

  useEffect(() => {
    fetchTrainee();
  }, [traineeId]);

  const fetchTrainee = async () => {
    try {
      const res = await fetch(`/api/v1/evaluations/trainee/${traineeId}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to load evaluation');
      }
      const data = await res.json();
      setTrainee(data.trainee);
      setAlreadySubmitted(data.alreadySubmitted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate all rating items filled
    for (const item of EVALUATION_ITEMS) {
      if (!form[item.key]) {
        alert(`Please rate: "${item.label}"`);
        return;
      }
    }
    if (!form.recommend) {
      alert('Please select whether you would recommend this training.');
      return;
    }
    if (!form.overall_rating) {
      alert('Please select an overall rating.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/v1/evaluations/trainee/${traineeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to submit');
      }
      setSubmitted(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={s.spinner} />
            <p style={{ color: '#6b6b6b', marginTop: '16px' }}>Loading evaluation form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>!</div>
            <h2 style={{ color: '#C0392B', marginBottom: '8px' }}>Unable to Load</h2>
            <p style={{ color: '#6b6b6b' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (alreadySubmitted || submitted) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.header}>
            <div style={s.logo}>ATA</div>
            <div>
              <div style={s.headerTitle}>NEOM Hospital</div>
              <div style={s.headerSub}>Academic & Training Affairs</div>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#e8f5e8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '1.8rem', color: '#6B8063' }}>
              &#10003;
            </div>
            <h2 style={{ color: '#2B362E', marginBottom: '8px' }}>
              {submitted ? 'Thank You!' : 'Already Submitted'}
            </h2>
            <p style={{ color: '#6b6b6b', fontSize: '0.9rem' }}>
              {submitted
                ? 'Your evaluation has been submitted successfully. Thank you for your feedback.'
                : 'An evaluation has already been submitted for this rotation. Thank you.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.logo}>ATA</div>
          <div>
            <div style={s.headerTitle}>NEOM Hospital</div>
            <div style={s.headerSub}>Academic & Training Affairs</div>
          </div>
        </div>

        {/* Title */}
        <div style={s.titleArea}>
          <h1 style={s.title}>Hosted Trainee End of Rotation Survey</h1>
          <p style={s.subtitle}>Short & Informative Feedback Form</p>
        </div>

        {/* Trainee Info */}
        <div style={s.infoGrid}>
          <div style={s.infoItem}>
            <span style={s.infoLabel}>Trainee Name</span>
            <span style={s.infoValue}>{trainee.trainee_name}</span>
          </div>
          <div style={s.infoItem}>
            <span style={s.infoLabel}>Program / Specialty</span>
            <span style={s.infoValue}>{trainee.training_type}</span>
          </div>
          <div style={s.infoItem}>
            <span style={s.infoLabel}>Department</span>
            <span style={s.infoValue}>{trainee.department}</span>
          </div>
          <div style={s.infoItem}>
            <span style={s.infoLabel}>Rotation Period</span>
            <span style={s.infoValue}>{trainee.training_period || `${trainee.start_date} to ${trainee.end_date}`}</span>
          </div>
          <div style={s.infoItem}>
            <span style={s.infoLabel}>Supervisor</span>
            <span style={s.infoValue}>{trainee.supervisor_name || 'N/A'}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Rating Table */}
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Item</th>
                  {RATING_OPTIONS.map((r) => (
                    <th key={r} style={s.thCenter}>{r}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {EVALUATION_ITEMS.map((item, idx) => (
                  <tr key={item.key} style={idx % 2 === 1 ? { backgroundColor: '#faf8f4' } : undefined}>
                    <td style={s.td}>{item.label}</td>
                    {RATING_OPTIONS.map((opt) => (
                      <td key={opt} style={s.tdCenter}>
                        <label style={s.radioLabel}>
                          <input
                            type="radio"
                            name={item.key}
                            value={opt}
                            checked={form[item.key] === opt}
                            onChange={() => setForm({ ...form, [item.key]: opt })}
                            style={s.radio}
                          />
                          <span style={{
                            ...s.radioCircle,
                            ...(form[item.key] === opt ? s.radioCircleActive : {}),
                          }}>
                            {form[item.key] === opt && <span style={s.radioInner} />}
                          </span>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Text Questions */}
          <div style={s.section}>
            <label style={s.questionLabel}>What was the best part of your rotation?</label>
            <textarea
              style={s.textarea}
              rows={3}
              value={form.best_part}
              onChange={(e) => setForm({ ...form, best_part: e.target.value })}
              placeholder="Share your experience..."
            />
          </div>

          <div style={s.section}>
            <label style={s.questionLabel}>Any suggestions for improvement?</label>
            <textarea
              style={s.textarea}
              rows={3}
              value={form.suggestions}
              onChange={(e) => setForm({ ...form, suggestions: e.target.value })}
              placeholder="Your suggestions..."
            />
          </div>

          {/* Recommend */}
          <div style={s.section}>
            <label style={s.questionLabel}>Would you recommend this training experience to others?</label>
            <div style={s.optionRow}>
              {RECOMMEND_OPTIONS.map((opt) => (
                <label key={opt} style={{
                  ...s.optionChip,
                  ...(form.recommend === opt ? s.optionChipActive : {}),
                }}>
                  <input
                    type="radio"
                    name="recommend"
                    value={opt}
                    checked={form.recommend === opt}
                    onChange={() => setForm({ ...form, recommend: opt })}
                    style={{ display: 'none' }}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>

          {/* Overall Rating */}
          <div style={s.section}>
            <label style={s.questionLabel}>Overall Rating</label>
            <div style={s.optionRow}>
              {RATING_OPTIONS.map((opt) => {
                const colors = { Excellent: '#6B8063', Good: '#3498DB', Fair: '#D4A017', Poor: '#C0392B' };
                return (
                  <label key={opt} style={{
                    ...s.optionChip,
                    ...(form.overall_rating === opt ? { backgroundColor: colors[opt], color: '#fff', borderColor: colors[opt] } : {}),
                  }}>
                    <input
                      type="radio"
                      name="overall_rating"
                      value={opt}
                      checked={form.overall_rating === opt}
                      onChange={() => setForm({ ...form, overall_rating: opt })}
                      style={{ display: 'none' }}
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Submit */}
          <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
            <button type="submit" disabled={submitting} style={s.submitBtn}>
              {submitting ? 'Submitting...' : 'Submit Evaluation'}
            </button>
          </div>
        </form>

        {/* Footer */}
        <div style={s.footer}>
          <p>Thank you for your feedback.</p>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F5F0E8',
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 16px',
  },
  card: {
    maxWidth: '720px',
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    alignSelf: 'flex-start',
  },
  header: {
    background: 'linear-gradient(135deg, #2B362E 0%, #3d4d40 100%)',
    padding: '20px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: 'rgba(107, 128, 99, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '0.75rem',
    letterSpacing: '1px',
  },
  headerTitle: { color: '#ffffff', fontSize: '1rem', fontWeight: 700 },
  headerSub: { color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' },
  titleArea: {
    textAlign: 'center',
    padding: '24px 28px 8px',
  },
  title: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#2B362E',
    margin: '0 0 4px',
  },
  subtitle: {
    fontSize: '0.78rem',
    color: '#B2AB9C',
    margin: 0,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    padding: '16px 28px 20px',
  },
  infoItem: {
    padding: '10px 14px',
    backgroundColor: '#faf8f4',
    borderRadius: '8px',
    border: '1px solid #f0ece4',
  },
  infoLabel: {
    display: 'block',
    fontSize: '0.65rem',
    color: '#B2AB9C',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    fontWeight: 600,
    marginBottom: '2px',
  },
  infoValue: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#2B362E',
  },
  tableWrap: {
    padding: '0 28px',
    overflowX: 'auto',
    marginBottom: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.82rem',
  },
  th: {
    padding: '10px 12px',
    fontSize: '0.72rem',
    fontWeight: 600,
    textAlign: 'left',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    color: '#6b6b6b',
    borderBottom: '2px solid #e8e3da',
    backgroundColor: '#F5F0E8',
  },
  thCenter: {
    padding: '10px 8px',
    fontSize: '0.72rem',
    fontWeight: 600,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    color: '#6b6b6b',
    borderBottom: '2px solid #e8e3da',
    backgroundColor: '#F5F0E8',
  },
  td: {
    padding: '10px 12px',
    fontSize: '0.82rem',
    borderBottom: '1px solid #f0ece4',
    color: '#2B362E',
  },
  tdCenter: {
    padding: '10px 8px',
    textAlign: 'center',
    borderBottom: '1px solid #f0ece4',
  },
  radioLabel: {
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  radio: { display: 'none' },
  radioCircle: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid #d4d0c8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  radioCircleActive: {
    borderColor: '#6B8063',
    backgroundColor: '#f0f7ee',
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#6B8063',
  },
  section: {
    padding: '0 28px',
    marginBottom: '18px',
  },
  questionLabel: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#2B362E',
    marginBottom: '8px',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #e8e3da',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none',
    transition: 'border 0.2s',
    boxSizing: 'border-box',
  },
  optionRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  optionChip: {
    padding: '8px 20px',
    borderRadius: '20px',
    border: '1.5px solid #d4d0c8',
    backgroundColor: '#ffffff',
    color: '#2B362E',
    fontSize: '0.82rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    userSelect: 'none',
  },
  optionChipActive: {
    backgroundColor: '#6B8063',
    color: '#ffffff',
    borderColor: '#6B8063',
  },
  submitBtn: {
    padding: '12px 48px',
    backgroundColor: '#2B362E',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  footer: {
    textAlign: 'center',
    padding: '16px 28px',
    borderTop: '1px solid #f0ece4',
    color: '#B2AB9C',
    fontSize: '0.78rem',
    fontStyle: 'italic',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f0ece4',
    borderTop: '4px solid #6B8063',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto',
  },
};
